import { Component, input, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, forkJoin, map } from 'rxjs';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Process } from '../../../models/engineering/process';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { ValueAccessorDirective } from '../../../directives/value-accessor.directive';
import { ProcessProxyService } from '../../../proxies/item/process.proxy.service';
import { InputParameter } from '../../../models/engineering/input-parameter';
import { ItemDetailsFormGroup, ItemConsumableDetails } from '../../../models/engineering/item-consumable-details';
import { ItemMaster } from '../../../models/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { StepIdsWithOrder } from '../../../models/engineering/process';
import { Step } from '../../../models/engineering/step';
import _ from 'lodash';

@Component({
  selector: 'app-process-details-viewer',
  templateUrl: './process-details-viewer.component.html',
  styleUrl: './process-details-viewer.component.scss',
  hostDirectives: [ValueAccessorDirective],
})
export class ProcessDetailsViewerComponent {
  private readonly valueAccessor = inject(ValueAccessorDirective<Process[]>);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly processProxyService = inject(ProcessProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly stepProxyService = inject(StepProxyService);

  readonly processToAdd = new FormControl<Process | null>(null);
  readonly itemMastersCache = signal<Map<number, ItemMaster[]>>(new Map());
  readonly parametersControlsCache = new Map<number, FormControl<InputParameter[]>>();
  readonly itemDetailsFormGroupsCache = new Map<number, FormArray<FormGroup<ItemDetailsFormGroup>>>();
  readonly stepsControlsCache = new Map<number, FormControl<Step[]>>();
  readonly showPreview = signal<boolean>(false);
  readonly processesForTable = signal<Process[]>([]);
  private initialLoadDone = false;

  canEdit = input<boolean>(false);
  canEditParameters = input<boolean>(true);
  canEditItems = input<boolean>(true);
  canEditSteps = input<boolean>(true);
  inventoryTypeReference = input<string | undefined>('6866c33a245e3504d98d3a44');
  processTypeReference = input<string | undefined>(undefined);

  processes = new FormArray<FormControl<Process>>([]);

  formGroup = new FormGroup({
    processes: this.processes,
  });

  constructor() {
    this.onValueChanges();
  }

  onProcessSelect(event: AutoCompleteSelectEvent): void {
    const process = event.value as Process;
    if (process) {
      this.addProcess(process);
      this.processToAdd.setValue(null);
    }
  }

  private onValueChanges(): void {
    this.valueAccessor.value$.subscribe((processesData: Process[]) => {
      if (processesData && Array.isArray(processesData) && processesData.length > 0) {
        this.loadProcessesIntoForm(processesData);
      } else {
        this.processes.clear();
        this.clearCaches();
      }
    });

    this.processes.valueChanges.subscribe(() => {
      this.updateModel();
      this.updateProcessesForTable();
    });
  }

  private loadProcessesIntoForm(processesData: Process[]): void {
    this.processes.clear();
    this.clearCaches();

    processesData.forEach((process) => {
      const processControl = new FormControl<Process>(process, { nonNullable: true });
      this.processes.push(processControl);
      this.initializeProcessFormControls([process]);
    });

    if (!this.initialLoadDone && processesData.length > 0) {
      this.showPreview.set(true);
      this.initialLoadDone = true;
    }

    this.updateProcessesForTable();
  }

  private clearCaches(): void {
    this.parametersControlsCache.clear();
    this.itemDetailsFormGroupsCache.clear();
    this.stepsControlsCache.clear();
    this.itemMastersCache.set(new Map());
  }

  public fetchProcesses = (query: string): Observable<Process[]> =>
    this.processProxyService.searchProcesses({ processName: query }).pipe(
      map((response) => {
        const addedProcessIds = this.processes.controls.map((c) => c.value.processId);
        let filtered = (response.data || []).filter((process) => !addedProcessIds.includes(process.processId));

        // Filter by processTypeReference if provided
        const processTypeRef = this.processTypeReference();
        if (processTypeRef) {
          filtered = filtered.filter((process) => process.processTypeReference === processTypeRef);
        }

        return filtered;
      }),
    );

  addProcess(process: Process): void {
    if (!process) return;

    const alreadyAdded = this.processes.controls.some((c) => c.value.processId === process.processId);

    if (alreadyAdded) return;

    const processControl = new FormControl<Process>(process, { nonNullable: true });
    this.processes.push(processControl);

    this.initializeProcessFormControls([process]);

    this.updateModel();
  }

  private updateModel(): void {
    const processesArray: Process[] = this.processes.controls.map((control) => control.value);
    this.valueAccessor.notifyValueChange(processesArray);
    this.valueAccessor.notifyTouched();
  }

  removeProcess(index: number): void {
    if (index >= 0 && index < this.processes.length) {
      const processId = this.processes.at(index).value.processId;

      this.processes.removeAt(index);

      this.parametersControlsCache.delete(processId);
      this.itemDetailsFormGroupsCache.delete(processId);
      this.stepsControlsCache.delete(processId);
      this.itemMastersCache.update((cache) => {
        const newCache = new Map(cache);
        newCache.delete(processId);
        return newCache;
      });

      this.updateModel();
    }
  }

  getProcessName(process: Process): string {
    return this.localeHelper.getLocalizedValue(process.name);
  }

  private initializeProcessFormControls(processes: Process[]): void {
    processes.forEach((process) => {
      if (!this.parametersControlsCache.has(process.processId)) {
        const control = new FormControl<InputParameter[]>(process.additionalParameters || [], {
          nonNullable: true,
        });
        this.parametersControlsCache.set(process.processId, control);

        control.valueChanges.subscribe((params) => {
          this.updateProcessProperty(process.processId, 'additionalParameters', params);
        });
      }

      if (!this.stepsControlsCache.has(process.processId)) {
        if (process.stepIdsWithOrder && process.stepIdsWithOrder.length > 0) {
          this.loadStepsForProcess(process.processId, process.stepIdsWithOrder);
        } else {
          const control = new FormControl<Step[]>([], {
            nonNullable: true,
          });
          this.stepsControlsCache.set(process.processId, control);
          this.subscribeToStepsChanges(process.processId, control);
        }
      }

      if (!this.itemDetailsFormGroupsCache.has(process.processId)) {
        const emptyFormArray = new FormArray<FormGroup<ItemDetailsFormGroup>>([]);
        this.itemDetailsFormGroupsCache.set(process.processId, emptyFormArray);

        emptyFormArray.valueChanges.subscribe((itemDetails) => {
          const convertedItems: ItemConsumableDetails[] = itemDetails.map((item) => ({
            itemCode: item.item?.itemCode || '',
            isPreferred: item.isPreferred ?? false,
            ingredientUnit: item.ingredientUnit!,
            scalingMethod: item.scalingMethod!,
            quantity: item.quantity ?? 0,
          }));
          this.updateProcessProperty(process.processId, 'itemDetails', convertedItems);
        });

        if (process.itemDetails && process.itemDetails.length > 0) {
          const itemCodes = process.itemDetails.map((item) => item.itemCode);
          this.loadItemMastersForProcessSync(process.processId, itemCodes, process.itemDetails);
        }
      }
    });
  }

  private subscribeToStepsChanges(processId: number, control: FormControl<Step[]>): void {
    control.valueChanges.subscribe((steps) => {
      const stepIdsWithOrder = steps.map((step, index) => ({
        stepId: step.stepId,
        order: index + 1,
        step: step,
      }));
      this.updateProcessProperty(processId, 'stepIdsWithOrder', stepIdsWithOrder);
    });
  }

  private updateProcessProperty<K extends keyof Process>(processId: number, property: K, value: Process[K]): void {
    const processIndex = this.processes.controls.findIndex((c) => c.value.processId === processId);

    if (processIndex !== -1) {
      const currentProcess = this.processes.at(processIndex).value;
      const updatedProcess = {
        ...currentProcess,
        [property]: value,
      };
      this.processes.at(processIndex).setValue(updatedProcess, { emitEvent: true });
    }
  }

  getParametersControl(process: Process): FormControl<InputParameter[]> {
    return this.parametersControlsCache.get(process.processId) || new FormControl<InputParameter[]>([], { nonNullable: true });
  }

  getStepsControl(process: Process): FormControl<Step[]> {
    return this.stepsControlsCache.get(process.processId) || new FormControl<Step[]>([], { nonNullable: true });
  }

  private loadStepsForProcess(processId: number, stepIdsWithOrder: StepIdsWithOrder[]): void {
    if (stepIdsWithOrder.length === 0) {
      const control = new FormControl<Step[]>([], { nonNullable: true });
      this.stepsControlsCache.set(processId, control);
      this.subscribeToStepsChanges(processId, control);
      return;
    }

    // Separar steps que ya tienen datos (del snapshot) de los que necesitan cargarse
    const stepsWithData: Step[] = [];
    const stepIdsToLoad: number[] = [];

    stepIdsWithOrder.forEach((swo) => {
      if (swo.step) {
        // Ya tenemos el step completo del snapshot
        stepsWithData.push(swo.step);
      } else {
        // Solo tenemos el stepId, necesitamos cargarlo
        stepIdsToLoad.push(swo.stepId);
      }
    });

    // Si todos los steps ya tienen datos, no hacer llamados a la API
    if (stepIdsToLoad.length === 0) {
      const control = new FormControl<Step[]>(stepsWithData, {
        nonNullable: true,
      });
      this.stepsControlsCache.set(processId, control);
      this.subscribeToStepsChanges(processId, control);

      const updatedStepIdsWithOrder = stepsWithData.map((step, index) => ({
        stepId: step.stepId,
        order: index + 1,
        step: step,
      }));
      this.updateProcessProperty(processId, 'stepIdsWithOrder', updatedStepIdsWithOrder);
      return;
    }

    // Cargar solo los steps que faltan
    const uniqueStepIdsToLoad = _.uniq(stepIdsToLoad);
    const stepRequests = uniqueStepIdsToLoad.map((stepId) => this.stepProxyService.getStepById(stepId.toString()));

    forkJoin(stepRequests).subscribe({
      next: (responses) => {
        const loadedStepsMap = new Map<number, Step>();
        responses.forEach((response) => {
          if (response.data) {
            loadedStepsMap.set(response.data.stepId, response.data);
          }
        });

        // Combinar steps del snapshot con los cargados, manteniendo el orden original
        const orderedSteps = stepIdsWithOrder
          .map((swo) => swo.step || loadedStepsMap.get(swo.stepId))
          .filter((step): step is Step => !_.isNil(step));

        const control = new FormControl<Step[]>(orderedSteps, {
          nonNullable: true,
        });
        this.stepsControlsCache.set(processId, control);
        this.subscribeToStepsChanges(processId, control);

        const updatedStepIdsWithOrder = orderedSteps.map((step, index) => ({
          stepId: step.stepId,
          order: index + 1,
          step: step,
        }));
        this.updateProcessProperty(processId, 'stepIdsWithOrder', updatedStepIdsWithOrder);
      },
      error: () => {
        const control = new FormControl<Step[]>([], { nonNullable: true });
        this.stepsControlsCache.set(processId, control);
        this.subscribeToStepsChanges(processId, control);
      },
    });
  }

  getItemDetailsFormGroupsForProcess(process: Process): FormArray<FormGroup<ItemDetailsFormGroup>> {
    return this.itemDetailsFormGroupsCache.get(process.processId) || new FormArray<FormGroup<ItemDetailsFormGroup>>([]);
  }

  private loadItemMastersForProcessSync(processId: number, itemCodes: string[], itemDetails: ItemConsumableDetails[]): void {
    this.itemMasterProxyService.getItemCodesBatch({ itemCodes }).subscribe({
      next: (response) => {
        if (response.data) {
          const itemsWithLocalized = response.data.map((item: ItemMaster) => ({
            ...item,
            itemDisplayNameLocalized: this.localeHelper.getLocalizedValue(item.itemDisplayName),
          }));

          this.itemMastersCache.update((cache) => {
            const newCache = new Map(cache);
            newCache.set(processId, itemsWithLocalized);
            return newCache;
          });

          const formArray = this.itemDetailsFormGroupsCache.get(processId);
          if (formArray) {
            formArray.clear();
            itemDetails.forEach((itemDetail) => {
              const item = itemsWithLocalized.find((i) => i.itemCode === itemDetail.itemCode);
              if (item) {
                formArray.push(this.createItemDetailFormGroup(itemDetail, item));
              }
            });
          }
        }
      },
    });
  }

  private createItemDetailFormGroup(itemDetail: ItemConsumableDetails, item: ItemMaster): FormGroup<ItemDetailsFormGroup> {
    return new FormGroup<ItemDetailsFormGroup>({
      item: new FormControl<ItemMaster>(item, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isPreferred: new FormControl<boolean>(itemDetail.isPreferred ?? false, {
        nonNullable: true,
      }),
      ingredientUnit: new FormControl(itemDetail.ingredientUnit ?? null),
      scalingMethod: new FormControl(itemDetail.scalingMethod ?? null),
      quantity: new FormControl(itemDetail.quantity ?? 0),
    });
  }

  togglePreview(): void {
    this.showPreview.update((value) => !value);
  }

  private updateProcessesForTable(): void {
    const processesWithCurrentData = this.processes.controls.map((control) => {
      const process = control.value;
      const parametersControl = this.parametersControlsCache.get(process.processId);

      // Asegurarse de que additionalParameters esté actualizado
      if (parametersControl) {
        return {
          ...process,
          additionalParameters: parametersControl.value,
        };
      }

      return process;
    });

    this.processesForTable.set(processesWithCurrentData);
  }
}
